import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { useAuth } from '~/routes/components/AuthProvider'
import { StyledMenuItem } from '../../CloudStorage/style'
import dayjs from 'dayjs'
import { getDateWithoutTime, handleErrors, parseDate } from '~/utils/helpers'
import { RouteTypes } from '~/routes/constants'
import { FormGroup, Segment, Tab, TabPane } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import LanguageTab from './LanguageTab'
import FarmerGroupInfo from './FarmerGroupInfo'
import Periods from './Periods'
import Dates from './Dates'
import { CoolFarmService } from '~/api/services/coolFarm'

const dateFields = [
  {
    key: 'start',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    key: 'end',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    key: 'periodFrom',
    format: 'DD.MM.YYYY'
  },
  {
    key: 'periodTo',
    format: 'DD.MM.YYYY'
  }
]

const CreateEditCoolFarm: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const navigate = useNavigate()
  const methods = useForm({
    mode: 'onSubmit'
  })
  const { handleSubmit, control, reset, getValues, setValue, watch, formState } = methods

  const watchPeriodFrom = watch('periodFrom')
  const watchPeriodTo = watch('periodTo')
  const watchStartDate = watch('start')
  const watchEndDate = watch('end')
  
  const [isBusy, setIsBusy] = useState(false)
  const [activeIndex, setActiveIndex] = useState<string | number>(0)
  const [options, setOptions] = useState(null)

  useEffect(() => {
    fetchCoolFarmData()
  }, [])

  useEffect(() => {
    if (watchPeriodFrom && watchPeriodTo) {
      const periodFrom = getDateWithoutTime(watchPeriodFrom)
      const periodTo = getDateWithoutTime(watchPeriodTo)
      if (dayjs(periodTo).diff(dayjs(periodFrom)) <= 0) {
        setValue('periodTo', getDayAhead(watchPeriodTo))
      }
    }
  }, [watchPeriodFrom])

  useEffect(() => {
    if (formState.isSubmitted) {
      if (Object.keys(formState.errors).length > 0) {
        notify(t('Please fill in all required fields'), 'error')
      }
    }

  }, [formState.isSubmitted])

  function getMenuItem(pane, index) {
    const languagesErrors = formState.errors?.languages;
    const paneHasError = index in (languagesErrors || {});

    return <StyledMenuItem
      key={pane}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{pane}</div>
    </StyledMenuItem>
  }

  async function fetchCoolFarmData() {
    setIsBusy(true);
    try {
      const responseOptions = await CoolFarmService.getCoolFarmOptionsData()
      const { languages } = responseOptions.data
      if (id) {
        const response = await CoolFarmService.getCoolFarmFootprint(id)
        const languagesEdit = response.data.languages
        languages.map((language) => {
          const indexLang = languagesEdit.findIndex(languageEd => languageEd.id === language.id)
          if (indexLang < 0) {
            languagesEdit.push({
              id: language.id,
              title: '',
              description: ''
            })
          } else {
            languagesEdit[indexLang].order = language.order
          }
        })
        languagesEdit.sort((a, b) => a.order - b.order)
        languagesEdit.forEach(lang => delete lang.order)
        response.data.languages = languagesEdit
        dateFields.forEach(dateField => response.data[dateField.key] = dayjs(new Date(response.data[dateField.key])).format(dateField.format))

        const responseFarmersList = await CoolFarmService.getFarmerList({
          periodFrom: convertDate(response.data.periodFrom),
          periodTo: convertDate(response.data.periodTo)
        })
        setOptions({ ...responseOptions.data, farmersList: responseFarmersList.data.map(farmer => ({ value: farmer.id, text: farmer.name })) })
        reset(response.data)
      } else {
        const languagesNew = []
        languages.map((language) => {
          languagesNew.push({
            id: language.id,
            title: '',
            description: ''
          })
        })

        const responseFarmersList = await CoolFarmService.getFarmerList({
          periodFrom: null,
          periodTo: null
        })
        setOptions({ ...responseOptions.data, farmersList: responseFarmersList.data.map(farmer => ({ value: farmer.id, text: farmer.name })) })
        setValue('languages', languagesNew)
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function convertDate(date) {
    const [day, month, yearAndTime] = date.split('.')
    const year = yearAndTime?.split(' ')[0]
    return (`${year}-${month}-${day}`)
  }

  function convertDateWithTime(date) {
    const [day, month, yearAndTime] = date.split('.')
    const year = yearAndTime?.split(' ')[0]
    const time = yearAndTime?.split(' ')[1]
    return (`${year}-${month}-${day} ${time}`)
  }

  function removeIfNotBase64(languageArray) {
    languageArray.forEach(language => {
      if (!language.file) {
        language.filename = null
        language.originalFilename = null
      }
    })
    return languageArray
  }

  async function onSubmit(data) {
    setIsBusy(true)
    data.periodFrom = convertDate(watchPeriodFrom)
    data.periodTo = convertDate(watchPeriodTo)
    data.dateFrom = convertDateWithTime(watchStartDate)
    data.dateTo = convertDateWithTime(watchEndDate)
    //remove attachment if it is not base64 (api issue)
    data.languages = removeIfNotBase64(data.languages)
    try {
      if (id) {
        await CoolFarmService.updateCoolFarmFootprint(id, data)
        notify(t('You have successfully updated the data collection for the Cool Farm GHG emissions calculation'), 'success')
      } else {
        await CoolFarmService.createCoolFarmFootprint(data)
        notify(t('You have successfully created the data collection for the Cool Farm GHG emissions calculation'), 'success')
      }
      navigate(RouteTypes.COOL_FARM)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderLanguagesForm() {
    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        renderActiveOnly={false}
        onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
        panes={options?.languages?.map((language, languageIndex) => {
          const langIndex = getValues('languages').findIndex(langValue => langValue.id === language.id)
          return {
            menuItem: getMenuItem(language.label, langIndex),
            pane: <TabPane
              active={languageIndex === activeIndex}
              style={{ border: 'none' }}
              key={language?.id}
            >
              <LanguageTab
                index={langIndex}
                language={language}
                languages={getValues('languages')}
              />
            </TabPane>
          };
        })}
      />
    );
  }

  function getDayAhead(date) {
    const inputDate = parseDate(date)
    const parsedDate = dayjs(inputDate, { format: 'DD.MM.YYYY' });
    if (parsedDate.isValid()) {
      const previousDay = parsedDate.add(1, 'day');
      return previousDay.format('DD.MM.YYYY');
    }
    return parsedDate.format('DD.MM.YYYY')
  }

  function renderForm() {
    return <>
      <FormGroup widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'type'}
          upward
          rules={{
            required: {
              value: true,
              message: t('Please select type')
            }
          }}
          placeholder={t('Please select type')}
          label={t('Cool Farm calculation logic')}
          options={options?.types.map(type => ({ text: type.label, value: type.key })) || []}
        />
        <FormFields.Select
          control={control}
          name={'farmerGroups'}
          upward
          rules={{
            required: {
              value: true,
              message: t('Please select farmer groups')
            }
          }}
          multiple
          placeholder={t('Please select farmer group')}
          label={t('Farmer groups')}
          options={options?.farmersList || []}
        />
      </FormGroup>
      <Periods
        languageLocal={languageLocal}
      />
      <FarmerGroupInfo/>
      <Dates
        languageLocal={languageLocal}
      />
    </>
  }

  const title = id ? t('Update Cool Farm data collection') : t('Creation of Cool Farm data collection')

  return <Layout
    title={<span dangerouslySetInnerHTML={{ __html: title }} />}
    isBusy={isBusy}
    onSubmit={handleSubmit(onSubmit)}
    action={id ? t('Update') : t('Save')}
    segmented
    close={RouteTypes.CO2_CLIMATE}
    isCo2Loader={true}
  >
    <FormProvider {...methods}>
    {options && !isBusy && <>
      <Segment>
        {renderLanguagesForm()}
      </Segment>
      <Segment style={{ marginTop: '3em' }}>
        {renderForm()}
      </Segment>
    </>
    }
    </FormProvider>
  </Layout>
}
export default CreateEditCoolFarm